<template>
  <transition name="login-switch" mode="out-in">
    <TransicionLogin
      v-if="showGameLoading"
      key="loading"
      text="Conectando con Google..."
    />

    <v-container
      v-else
      key="login"
      class="login-page fill-height d-flex align-center justify-center"
      fluid
    >
      <div class="login-bg-grid"></div>

      <!-- Tarjeta de acceso -->
      <v-card
        class="login-card pa-6"
        max-width="430"
        width="100%"
      >
        <div class="d-flex justify-center mb-5">
          <v-img
            :src="require('@/assets/logo_electroliga.svg')"
            contain
            max-width="270"
            class="login-logo"
            alt="Electroliga"
          />
        </div>

        <div class="login-title">
          Entrar
        </div>

        <div class="login-subtitle">
          Accede a tu compañía energética y compite en el mercado.
        </div>

        <v-alert
          v-if="error"
          type="error"
          dense
          class="mb-3 login-alert"
        >
          {{ error }}
        </v-alert>

        <!-- Login -->
        <v-btn
          block
          class="login-btn"
          :loading="loading"
          :disabled="loading"
          @click="doGoogleAuth('login')"
        >
          <span class="material-symbols-outlined mr-2">
            login
          </span>

          Iniciar sesión con Google
        </v-btn>

        <!-- Tutorial -->
        <v-btn
          block
          outlined
          class="tutorial-btn mt-4"
          :disabled="loading"
          @click="abrirTutorial"
        >
          <span class="material-symbols-outlined mr-2">
            school
          </span>

          Cómo jugar
        </v-btn>
      </v-card>

      <!-- Diálogo del tutorial -->
      <v-dialog
        v-model="showTutorial"
        :fullscreen="$vuetify.breakpoint.smAndDown"
        max-width="920"
        content-class="tutorial-login-dialog"
        scrollable
      >
        <v-card class="tutorial-login-card">
          <v-toolbar
            flat
            class="tutorial-toolbar"
          >
            <v-toolbar-title class="tutorial-toolbar-title">
              Cómo jugar a Electroliga
            </v-toolbar-title>

            <v-spacer />

            <v-btn
              icon
              class="tutorial-close-btn"
              aria-label="Cerrar tutorial"
              @click="cerrarTutorial"
            >
              <span class="material-symbols-outlined">
                close
              </span>
            </v-btn>
          </v-toolbar>

          <v-card-text class="tutorial-card-content">
            <!--
              Se monta únicamente cuando el diálogo está abierto.
              Al cerrar se destruye también la instancia de Phaser.
            -->
            <Tutorial
              v-if="showTutorial"
              :key="tutorialKey"
            />
          </v-card-text>
        </v-card>
      </v-dialog>
    </v-container>
  </transition>
</template>

<script>
import { useGameStore } from "@/stores/game";
import TransicionLogin from "./TransicionLogin.vue";
import Tutorial from "../components/Tutorial.vue";

export default {
  name: "Login",

  components: {
    TransicionLogin,
    Tutorial,
  },

  data() {
    return {
      loading: false,
      showGameLoading: false,
      error: null,

      store: useGameStore(),

      showTutorial: false,
      tutorialKey: 0,
    };
  },

  methods: {
    abrirTutorial() {
      if (this.loading) {
        return;
      }

      // Hace que cada apertura comience con una instancia nueva.
      this.tutorialKey += 1;
      this.showTutorial = true;
    },

    cerrarTutorial() {
      this.showTutorial = false;
    },

    doGoogleAuth(mode) {
  if (this.loading) {
    return;
  }

  this.error = null;
  this.loading = true;
  this.showGameLoading = true;
  this.showTutorial = false;

  try {
    const configuredBaseUrl =
      process.env.VUE_APP_API_BASE_URL || "";

    const baseUrl = configuredBaseUrl.replace(/\/+$/, "");

    const url =
      `${baseUrl}/auth/google?mode=${encodeURIComponent(mode)}`;

    window.setTimeout(() => {
      window.location.href = url;
    }, 900);
  } catch (error) {
    console.error(
      "Error iniciando autenticación con Google:",
      error
    );

    this.loading = false;
    this.showGameLoading = false;

    this.error =
      mode === "login"
        ? "No se pudo iniciar sesión con Google."
        : "No se pudo completar el registro con Google.";
  }
},
  },
};
</script>

<style scoped>
.login-page {
  position: relative;
  min-height: 100vh;
  overflow: hidden;

  background:
    radial-gradient(
      circle at 20% 15%,
      rgba(34, 197, 94, 0.22),
      transparent 28%
    ),
    radial-gradient(
      circle at 85% 20%,
      rgba(56, 189, 248, 0.22),
      transparent 30%
    ),
    linear-gradient(
      135deg,
      #06111f 0%,
      #0f172a 55%,
      #111827 100%
    );
}

.login-bg-grid {
  position: absolute;
  inset: 0;

  background-image:
    linear-gradient(
      rgba(255, 255, 255, 0.04) 1px,
      transparent 1px
    ),
    linear-gradient(
      90deg,
      rgba(255, 255, 255, 0.04) 1px,
      transparent 1px
    );

  background-size: 44px 44px;
  opacity: 0.55;
  pointer-events: none;
}

.login-card {
  position: relative;
  z-index: 1;

  border: 1px solid rgba(255, 255, 255, 0.16);
  border-radius: 30px !important;

  color: #ffffff;
  background: rgba(255, 255, 255, 0.09) !important;

  box-shadow:
    0 28px 90px rgba(0, 0, 0, 0.48);

  backdrop-filter: blur(18px);
  -webkit-backdrop-filter: blur(18px);
}

.login-logo {
  filter:
    drop-shadow(
      0 0 18px rgba(56, 189, 248, 0.26)
    );
}

.login-title {
  color: #ffffff;

  font-family: "Orbitron", sans-serif !important;
  font-size: 26px;
  font-weight: 800;
  letter-spacing: 0.6px;
  text-align: center;
}

.login-subtitle {
  max-width: 330px;
  margin: 10px auto 26px;

  color: rgba(255, 255, 255, 0.72);

  font-size: 14px;
  line-height: 1.45;
  text-align: center;
}

.login-alert {
  border-radius: 16px;
}

/* Botón de inicio de sesión */
.login-btn {
  height: 46px !important;
  border-radius: 999px !important;

  color: #06111f !important;
  background:
    linear-gradient(
      90deg,
      #22c55e,
      #38bdf8
    ) !important;

  font-weight: 800 !important;
  letter-spacing: 0.4px;
  text-transform: none !important;

  box-shadow:
    0 0 28px rgba(56, 189, 248, 0.42);

  transition:
    transform 0.2s ease,
    box-shadow 0.2s ease !important;
}

.login-btn:hover {
  transform: translateY(-1px);

  box-shadow:
    0 0 34px rgba(56, 189, 248, 0.56);
}

/* Botón de tutorial */
.tutorial-btn {
  height: 46px !important;

  border: 2px solid rgba(56, 189, 248, 0.8) !important;
  border-radius: 999px !important;

  color: #ffffff !important;
  background: rgba(6, 17, 31, 0.38) !important;

  font-family: "Orbitron", sans-serif !important;
  font-size: 13px !important;
  font-weight: 700 !important;
  letter-spacing: 0.4px !important;
  text-transform: none !important;

  transition:
    background-color 0.2s ease,
    border-color 0.2s ease,
    transform 0.2s ease,
    box-shadow 0.2s ease !important;
}

.tutorial-btn:hover {
  border-color: #22c55e !important;
  background: rgba(56, 189, 248, 0.18) !important;

  transform: translateY(-1px);

  box-shadow:
    0 5px 18px rgba(56, 189, 248, 0.2);
}

/* Tarjeta del diálogo */
.tutorial-login-card {
  display: flex;
  flex-direction: column;

  width: 100%;
  max-height: calc(100vh - 32px);

  overflow: hidden;

  border-radius: 20px !important;
  background: #ffffff !important;
}

.tutorial-toolbar {
  flex: 0 0 auto;

  background:
    linear-gradient(
      90deg,
      rgba(34, 197, 94, 0.12),
      rgba(56, 189, 248, 0.14)
    ) !important;

  border-bottom:
    1px solid rgba(49, 99, 95, 0.15);
}

.tutorial-toolbar-title {
  color: #31635f;

  font-family: "Orbitron", sans-serif !important;
  font-size: 18px !important;
  font-weight: 700;
}

.tutorial-close-btn {
  color: #31635f !important;
}

.tutorial-card-content {
  flex: 1 1 auto;

  padding: 0 !important;
  overflow: auto;
  background: #ffffff;
}

/* Transición entre login y carga */
.login-switch-enter-active,
.login-switch-leave-active {
  transition:
    opacity 0.45s ease,
    transform 0.45s ease;
}

.login-switch-enter,
.login-switch-leave-to {
  opacity: 0;
  transform: scale(0.97);
}

@media (max-width: 600px) {
  .login-page {
    padding:
      max(12px, env(safe-area-inset-top))
      max(12px, env(safe-area-inset-right))
      max(12px, env(safe-area-inset-bottom))
      max(12px, env(safe-area-inset-left)) !important;
  }

  .login-card {
    padding: 24px 20px !important;
    border-radius: 24px !important;
  }

  .login-title {
    font-size: 22px;
  }

  .login-subtitle {
    font-size: 13px;
  }

  .login-btn,
  .tutorial-btn {
    height: 44px !important;
    font-size: 12px !important;
  }

  .tutorial-login-card {
    height: 100vh;
    height: 100dvh;
    max-height: none;

    border-radius: 0 !important;
  }

  .tutorial-toolbar-title {
    font-size: 14px !important;
  }
}
</style>

<style>
.tutorial-login-dialog {
  overflow: hidden !important;
  border-radius: 20px !important;
}

@media (max-width: 600px) {
  .tutorial-login-dialog {
    width: 100vw !important;
    height: 100vh !important;
    height: 100dvh !important;

    max-width: none !important;
    max-height: none !important;

    margin: 0 !important;
    border-radius: 0 !important;
  }
}
</style>